import React, { Suspense, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useContext } from 'react';

import { useIntl } from 'react-intl';
import { useMergeRefs } from 'use-callback-ref';

import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import {
	type FileDetails,
	type FileIdentifier,
	type FileState,
	globalMediaEventEmitter,
	type Identifier,
	type ImageResizeMode,
	type MediaBlobUrlAttrs,
	RECENTS_COLLECTION,
	imageResizeModeToFileImageMode,
	isProcessedFileState,
	type NonErrorFileState,
	isErrorFileState,
	toCommonMediaClientError,
	type AuthProviderSucceededEventPayload,
	type AuthProviderFailedEventPayload,
} from '@atlaskit/media-client';
import { useFileState } from '@atlaskit/media-client-react/use-file-state';
import { useMediaClient } from '@atlaskit/media-client-react/use-media-client';
import {
	mapSsrMediaItemToFileState,
	type SsrMediaItem,
} from '@atlaskit/media-client/ssr-media-item';
import {
	isMimeTypeSupportedByBrowser,
	type MediaFeatureFlags,
	type MediaTraceContext,
	type NumericalCardDimensions,
	type SSR,
	isVideoMimeTypeSupportedByBrowser,
	getRandomTelemetryId,
} from '@atlaskit/media-common';
import type { MediaFilePreviewErrorPrimaryReason } from '@atlaskit/media-file-preview/media-file-preview-error';
import type { MediaFilePreview } from '@atlaskit/media-file-preview/types';
import { useFilePreview } from '@atlaskit/media-file-preview/use-file-preview';
import type { ProcessingFailedState } from '@atlaskit/media-state/file-state';
import { AbuseModal } from '@atlaskit/media-ui/abuseModal';
import {
	MediaViewer,
	type ViewerOptionsProps,
	type MediaViewerExtensions,
} from '@atlaskit/media-viewer';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { getActiveTrace } from '@atlaskit/react-ufo/get-active-trace';
import usePressTracing from '@atlaskit/react-ufo/use-press-tracing';

import { DateOverrideContext } from '../dateOverrideContext';
import { isSSRPreview } from '../isSSRPreview';
import { MediaCardError, type MediaCardErrorPrimaryReason } from '../MediaCardError';
import type {
	CardAppearance,
	CardDimensions,
	CardEventProps,
	CardStatus,
	FileStateFlags,
	TitleBoxIcon,
} from '../types';
import { fireMediaCardEvent } from '../utils/analytics/fireMediaCardEvent';
import { getAuthProviderFailedPayload } from '../utils/analytics/getAuthProviderFailedPayload';
import { getAuthProviderSucceededPayload } from '../utils/analytics/getAuthProviderSucceededPayload';
import { getDefaultCardDimensions } from '../utils/cardDimensions';
import { generateUniqueId } from '../utils/generateUniqueId';
import { getMediaCardCursor } from '../utils/getMediaCardCursor';
import { isKeyboardFocusEnteringElement } from '../utils/isKeyboardFocusEnteringElement';
import { getFileDetails } from '../utils/metadata';
import { resolveCardPreviewDimensions } from '../utils/resolveCardPreviewDimensions';
import {
	shouldPerformanceBeSampled,
	useMediaCardUfoExperience,
	type SSRPreviewInfo,
} from '../utils/ufoExperiences';
import { useCurrentValueRef } from '../utils/useCurrentValueRef';
import { usePrevious } from '../utils/usePrevious';
import { ViewportDetector } from '../utils/viewportDetector';
import type { CardAction } from './actions';
import { CardView } from './cardView';
import { createDownloadAction } from './createDownloadAction';
import { fireDownloadFailedEvent } from './fireDownloadFailedEvent';
import { fireDownloadSucceededEvent } from './fireDownloadSucceededEvent';
import { fireNonCriticalErrorEvent } from './fireNonCriticalErrorEvent';
import { fireOperationalEvent } from './fireOperationalEvent';
import { InlinePlayerLazy } from './inlinePlayerLazy';
import { performanceNow } from './performance';
import type { SsrItemDetails } from './types';

export interface FileCardProps extends CardEventProps {
	/** Overlay the media file. */
	readonly disableOverlay?: boolean;
	/** Resize the media to 'crop' | 'fit' | 'full-fit' | 'stretchy-fit'. */
	readonly resizeMode?: ImageResizeMode;
	/* Includes media features like caption, timestamp etc. */
	readonly featureFlags?: MediaFeatureFlags;
	/** Sets meida card appearance */
	readonly appearance?: CardAppearance;
	/** Custom media card dimension like width and height. */
	readonly dimensions?: CardDimensions;
	/** Original media card dimension like width and height. */
	readonly originalDimensions?: NumericalCardDimensions;
	/** Array of additional media card actions. */
	readonly actions?: Array<CardAction>;
	/** Enable media card selectable. */
	readonly selectable?: boolean;
	/** Renders media card as selected, if selected is true. */
	readonly selected?: boolean;
	/** Alternate text for an media card. */
	readonly alt?: string;
	/** ID for testing the media card. */
	readonly testId?: string;
	/** Sets the title box background color. */
	readonly titleBoxBgColor?: string;
	/** Sets the title box icon. */
	readonly titleBoxIcon?: TitleBoxIcon;
	/** Overrides the background color for media rendering (e.g. the default white background used for SVG transparency). */
	readonly backgroundColor?: React.CSSProperties['backgroundColor'];
	/** Indicates the media card is being generated by AI (e.g. Rovo image-create). */
	readonly isAIGenerating?: boolean;
	/** Marks the card as part of the CWR (create-with-Rovo) infographics flow. */
	readonly isCWR?: boolean;
	/**
	 * Drops the loading indicator and fades the preview in over the card's surface instead.
	 * Also behind `aifc_page_create_defer_generated_visuals`.
	 */
	readonly hasLoadingMotion?: boolean;
	/** Callback fired when the media card's image preview has rendered. */
	readonly onPreviewRender?: (fileId: string) => void;
	/** Instance of file identifier. */
	readonly identifier: FileIdentifier;
	/** Lazy loads the media file. */
	readonly isLazy?: boolean;
	/** Uses the inline player for media file. */
	readonly useInlinePlayer?: boolean;
	/** Uses media MediaViewer to preview the media file. */
	readonly shouldOpenMediaViewer?: boolean;
	/** Media file list to display in Media Viewer. */
	readonly mediaViewerItems?: Identifier[];
	/** Retrieve auth based on a given context. */
	readonly contextId?: string;
	/** Enables the download button for media file. */
	readonly shouldEnableDownloadButton?: boolean;
	/** Server-Side-Rendering modes are "server" and "client" */
	readonly ssr?: SSR;
	/** Disable tooltip for the card */
	readonly shouldHideTooltip?: boolean;
	/** Sets options for viewer **/
	readonly viewerOptions?: ViewerOptionsProps;
	/** Sets options for viewer **/
	readonly includeHashForDuplicateFiles?: boolean;
	/**
	 * @deprecated Use `ssrFileState` instead (Phase 5b). Will be removed in a future major.
	 */
	readonly ssrItemDetails?: SsrItemDetails;
	/**
	 * Pre-hydrated SSR metadata from a Relay fragment (Media Platform Phase 5b).
	 * When provided and `fg('platform_media_ssr_data_seed')` is on, the card seeds
	 * `useFileState` with this data and skips the `items()` API call for files
	 * whose `processingStatus` is `succeeded`.
	 * @see https://product-fabric.atlassian.net/browse/BMPT-7914
	 */
	readonly ssrFileState?: FileState;
	/**
	 * Raw SSR media item from a host payload (e.g. Confluence recorded media nodes).
	 * Used when `ssrFileState` is not provided. Converted to FileState internally
	 * when `fg('platform_media_ssr_data_seed')` is on.
	 */
	readonly ssrMediaItem?: SsrMediaItem;
	/** General Error handling include status errors and display errors*/
	readonly onError?: (
		reason: MediaFilePreviewErrorPrimaryReason | MediaCardErrorPrimaryReason,
	) => void;
	/** Extensions for the media viewer (e.g. comment button in header, sidebar with comment indicator). */
	readonly mediaViewerExtensions?: MediaViewerExtensions;
	/**
	 * Optional fallback fetcher to retrieve the media filename from another service.
	 * Workaround for #hot-301450 where media service is missing filenames for DC -> Cloud migrated media.
	 * Receives the file ID and should resolve to the filename string.
	 * TODO: Remove this prop when fallback-fetcher usage is sufficiently low.
	 */
	readonly fallbackMediaNameFetcher?: (id: string) => Promise<string>;
}

const traceContextRetriever = () => {
	const trace = getActiveTrace();
	if (trace) {
		return { traceId: trace.traceId, spanId: trace.spanId };
	} else {
		return {
			traceId: getRandomTelemetryId(),
		};
	}
};

export const FileCard = ({
	appearance = 'auto',
	resizeMode = 'crop',
	isLazy = true,
	disableOverlay = false,
	// Media Feature Flag defaults are defined in @atlaskit/media-common
	featureFlags = {},
	identifier,
	ssr,
	dimensions,
	originalDimensions,
	contextId,
	alt,
	actions,
	shouldEnableDownloadButton,
	useInlinePlayer,
	shouldOpenMediaViewer,
	onFullscreenChange,
	selectable,
	selected,
	testId,
	titleBoxBgColor,
	titleBoxIcon,
	backgroundColor,
	isAIGenerating,
	isCWR,
	hasLoadingMotion,
	onPreviewRender,
	shouldHideTooltip,
	mediaViewerItems,
	onClick,
	onMouseEnter,
	onFocus,
	videoControlsWrapperRef,
	viewerOptions,
	includeHashForDuplicateFiles,
	ssrItemDetails,
	ssrFileState,
	ssrMediaItem,
	onError,
	mediaViewerExtensions,
	fallbackMediaNameFetcher,
}: FileCardProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isAbuseModalOpen, setIsAbuseModalOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	//----------------------------------------------------------------//
	//------------ State, Refs & Initial Values ----------------------//
	//----------------------------------------------------------------//

	const pressTracing = usePressTracing('click-file-card');

	const mediaClient = useMediaClient();

	const [cardElement, setCardElement] = useState<HTMLDivElement | null>(null);

	// use reference here to avoid excessive calls.
	const onErrorRef = useCurrentValueRef(onError);

	const cardDimensions = dimensions || getDefaultCardDimensions(appearance);

	// Calculate the preview dimensions if card dimensions are "percentage" based
	const previewDimensions = useMemo(
		() =>
			// resolving dimensions is eventually an expensive operation if the dimensions are a percentage
			// thus needs to be memoized
			resolveCardPreviewDimensions({
				dimensions: cardDimensions,
				element: cardElement,
			}),
		[cardDimensions, cardElement],
	);

	const [isCardVisible, setIsCardVisible] = useState(!isLazy);

	// Note: mapMediaItemToFileState maps the fragment's processingStatus faithfully (including
	// processing/pending). The decision to skip polling is delegated to useFileState —
	// it skips subscription only when status === 'processed'. Do not add status guards here.
	// ssrFileState (Relay / explicit seed) wins over converting ssrMediaItem.
	const initialFileState = useMemo(
		() =>
			fg('platform_media_ssr_data_seed')
				? (ssrFileState ?? mapSsrMediaItemToFileState(ssrMediaItem))
				: undefined,
		[ssrFileState, ssrMediaItem],
	);

	const { fileState } = useFileState(identifier.id, {
		skipRemote: !isCardVisible,
		collectionName: identifier.collectionName,
		occurrenceKey: identifier.occurrenceKey,
		includeHashForDuplicateFiles,
		initialFileState,
	});

	const [fallbackMediaName, setFallbackMediaName] = useState<string | undefined>();
	const fallbackMediaNameFetchAttempted = useRef(false);
	const lastFetchedFileId = useRef<string | undefined>();

	const prevFileState: NonErrorFileState | undefined = usePrevious(
		fileState && isErrorFileState(fileState) ? undefined : fileState,
	);

	const fileStateValue: NonErrorFileState | undefined = useMemo(() => {
		if (fileState && !isErrorFileState(fileState)) {
			return fileState;
		}
		return prevFileState;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fileState]);

	useEffect(() => {
		// Reset fetch state when the file identity changes
		const currentId = fileStateValue?.id;
		fallbackMediaNameFetchAttempted.current = false;
		setFallbackMediaName(undefined);
		lastFetchedFileId.current = currentId;

		if (
			fileStateValue &&
			!fileStateValue.name &&
			fallbackMediaNameFetcher &&
			!fallbackMediaNameFetchAttempted.current
		) {
			fallbackMediaNameFetchAttempted.current = true;
			fallbackMediaNameFetcher(fileStateValue.id).then(
				(name) => {
					setFallbackMediaName(name);
				},
				() => {
					// Silently ignore fetch failures
				},
			);
		}
	}, [fileStateValue, fallbackMediaNameFetcher]);

	const dateOverrides = useContext(DateOverrideContext);
	const overridenDate = dateOverrides?.[identifier.id];

	//----------------------------------------------------------------//
	//------------ State, Refs & Initial Values ----------------------//
	//----------------------------------------------------------------//

	const internalOccurrenceKey = useMemo(() => generateUniqueId(), []);
	const timeElapsedTillCommenced = useMemo(() => performanceNow(), []);

	const fileStateFlagsRef = useRef<FileStateFlags>({
		wasStatusUploading: false,
		wasStatusProcessing: false,
	});

	const mediaViewerButtonRef = useRef<HTMLButtonElement>(null);

	// Generate unique traceId for file
	const traceContext = useMemo<MediaTraceContext>(traceContextRetriever, []);

	const [status, setStatus] = useState<CardStatus>('loading');

	const [isPlayingFile, setIsPlayingFile] = useState(false);
	const [shouldAutoplay, setShouldAutoplay] = useState(false);

	const [previewDidRender, setPreviewDidRender] = useState(false);

	const mediaBlobUrlAttrs = useMemo<MediaBlobUrlAttrs | undefined>(() => {
		const { id, collectionName: collection } = identifier;
		const { mimeType, name, size } = getFileDetails(identifier, fileStateValue);
		return contextId
			? {
					id,
					collection,
					contextId,
					mimeType,
					name,
					size,
					...(originalDimensions || previewDimensions),
					alt,
				}
			: undefined;
	}, [alt, previewDimensions, contextId, fileStateValue, identifier, originalDimensions]);

	const {
		preview,
		status: previewStatus,
		error: previewError,
		nonCriticalError,
		ssrReliability,
		onImageError: onImageErrorBase,
		onImageLoad: onImageLoadBase,
		getSsrScriptProps,
		copyNodeRef,
	} = useFilePreview({
		initialFileState: initialFileState,
		useSrcSet: true,
		mediaBlobUrlAttrs,
		resizeMode: imageResizeModeToFileImageMode(resizeMode),
		identifier,
		ssr,
		dimensions: previewDimensions,
		traceContext,
		skipRemote: !isCardVisible,
		source: 'mediaCard',
	});

	const shouldSendPerformanceEvent = useMemo(() => shouldPerformanceBeSampled(), []);

	// UFO experience hook - creates a unique experience instance per card
	const ufoExperience = useMediaCardUfoExperience({
		instanceId: internalOccurrenceKey,
		enabled: shouldSendPerformanceEvent,
	});

	const [error, setError] = useState<MediaCardError | undefined>();

	// CXP-2723 TODO: TEMPORARY VARIABLES
	const finalError =
		error ||
		(previewError &&
		previewError.primaryReason !== 'failed-processing' &&
		fileStateValue?.mimeType !== 'image/svg+xml'
			? previewError
			: undefined);

	const finalStatus = finalError
		? 'error'
		: status === 'failed-processing' && fileStateValue?.mimeType === 'image/svg+xml'
			? 'loading-preview'
			: status;

	// Experiment gate for the Confluence comments-in-media-viewer work. Only the
	// viewer-state persistence callbacks introduced by that series are gated —
	// `mediaViewerExtensions` itself is still forwarded to MediaViewer so the
	// previously shipped `sidebar` and `headerActions` keep working with the
	// experiment off.
	const { getMediaViewerSelectedItem, onSelectedItemChange }: MediaViewerExtensions =
		isExperimentEnabled('cc_comments_media_viewer_sidebar') ? (mediaViewerExtensions ?? {}) : {};

	const [mediaViewerSelectedItem, setMediaViewerSelectedItem] = useState<Identifier | null>(
		() => getMediaViewerSelectedItem?.(identifier) ?? null,
	);

	const openMediaViewer = useCallback(() => {
		setMediaViewerSelectedItem(identifier);
		onSelectedItemChange?.(identifier);
	}, [identifier, onSelectedItemChange]);

	const closeMediaViewer = useCallback(() => {
		setMediaViewerSelectedItem(null);
		onSelectedItemChange?.(null);
	}, [onSelectedItemChange]);

	const uploadProgressRef = useRef<number>();

	// Store latest auth provider event (emit with fireOperationalEventRef)
	const pendingAuthProviderEventRef = useRef<
		| { type: 'succeeded'; payload: AuthProviderSucceededEventPayload }
		| { type: 'failed'; payload: AuthProviderFailedEventPayload }
		| null
	>(null);

	const metadata = useMemo<FileDetails>(() => {
		const getProcessingStatusFromFileState = (status: FileState['status']) => {
			switch (status) {
				case 'processed':
					return 'succeeded';
				case 'processing':
					return 'running';
				case 'failed-processing':
					return 'failed';
			}
		};

		if (fg('dfo_attachments_late_render_fix')) {
			if (fileStateValue) {
				return {
					id: fileStateValue.id,
					name:
						fileStateValue.name || fallbackMediaName || (ssrItemDetails && ssrItemDetails.filename),
					size: fileStateValue.size,
					mimeType: fileStateValue.mimeType || (ssrItemDetails && ssrItemDetails.mimetype),
					createdAt: fileStateValue.createdAt || (ssrItemDetails && ssrItemDetails.createdDate),
					mediaType: fileStateValue.mediaType,
					processingStatus: getProcessingStatusFromFileState(fileStateValue.status),
					failReason:
						fileStateValue.status === 'failed-processing'
							? (fileStateValue as ProcessingFailedState).failReason
							: undefined,
				};
			} else {
				return {
					id: identifier.id,
					name: ssrItemDetails && ssrItemDetails.filename,
					mimeType: ssrItemDetails && ssrItemDetails.mimetype,
					createdAt: ssrItemDetails && ssrItemDetails.createdDate,
				};
			}
		} else {
			if (fileStateValue) {
				return {
					id: fileStateValue.id,
					name: fileStateValue.name || fallbackMediaName,
					size: fileStateValue.size,
					mimeType: fileStateValue.mimeType,
					createdAt: fileStateValue.createdAt,
					mediaType: fileStateValue.mediaType,
					processingStatus: getProcessingStatusFromFileState(fileStateValue.status),
					failReason:
						fileStateValue.status === 'failed-processing'
							? (fileStateValue as ProcessingFailedState).failReason
							: undefined,
				};
			} else {
				return {
					id: identifier.id,
				};
			}
		}
	}, [fileStateValue, identifier.id, ssrItemDetails, fallbackMediaName]);

	const fileAttributes = useMemo(() => {
		return {
			fileMediatype: metadata.mediaType,
			fileMimetype: metadata.mimeType,
			fileId: metadata.id,
			fileSize: metadata.size,
			fileStatus: fileStateValue?.status,
		};
	}, [fileStateValue?.status, metadata.id, metadata.mediaType, metadata.mimeType, metadata.size]);

	const downloadFn = useCallback(async () => {
		try {
			await mediaClient.file.downloadBinary(
				identifier.id,
				metadata.name,
				identifier.collectionName,
				traceContext,
			);
			fireDownloadSucceededEvent(
				createAnalyticsEvent,
				fileAttributes,
				traceContext,
				fileStateValue?.metadataTraceContext,
			);
		} catch (e) {
			const error = new MediaCardError('download', e as Error);
			fireDownloadFailedEvent(
				createAnalyticsEvent,
				fileAttributes,
				error,
				traceContext,
				fileStateValue?.metadataTraceContext,
			);
		}
	}, [
		createAnalyticsEvent,
		fileAttributes,
		fileStateValue?.metadataTraceContext,
		identifier.collectionName,
		identifier.id,
		mediaClient.file,
		metadata.name,
		traceContext,
	]);

	const computedActions = useMemo(() => {
		if (finalStatus === 'failed-processing' || shouldEnableDownloadButton) {
			const handler = async () => {
				if (!!fileStateValue?.abuseClassification) {
					setIsAbuseModalOpen(true);
				} else {
					await downloadFn();
				}
			};
			const downloadAction = createDownloadAction(
				{
					handler,
					isDisabled: mediaClient.config.enforceDataSecurityPolicy,
				},
				formatMessage,
			);
			return [downloadAction, ...(actions ?? [])];
		} else {
			return actions;
		}
	}, [
		actions,
		mediaClient.config.enforceDataSecurityPolicy,
		shouldEnableDownloadButton,
		finalStatus,
		formatMessage,
		fileStateValue?.abuseClassification,
		downloadFn,
	]);

	//----------------------------------------------------------------//
	//---------------------- Analytics  ------------------------------//
	//----------------------------------------------------------------//

	const fireOperationalEventRef = useCurrentValueRef(() => {
		const timeElapsedTillEvent = performanceNow();
		const durationSinceCommenced = timeElapsedTillEvent - timeElapsedTillCommenced;

		const performanceAttributes = {
			overall: {
				durationSincePageStart: timeElapsedTillEvent,
				durationSinceCommenced,
			},
		};
		createAnalyticsEvent &&
			fireOperationalEvent(
				createAnalyticsEvent,
				finalStatus,
				fileAttributes,
				performanceAttributes,
				ssrReliability,
				finalError,
				traceContext,
				fileStateValue?.metadataTraceContext,
				metadata.failReason,
			);

		// Emit stored auth provider events when card reaches final state
		if (
			createAnalyticsEvent &&
			pendingAuthProviderEventRef.current &&
			['complete', 'error', 'failed-processing'].includes(finalStatus) &&
			fg('platform_media_auth_provider_analytics')
		) {
			const authEvent = pendingAuthProviderEventRef.current;
			// Sample auth-provider-succeeded events at 10%
			const shouldSampleAuthProviderSucceeded = () => Math.random() < 0.1;

			if (authEvent.type === 'succeeded') {
				// Emit success events when card completes (or errors - auth can succeed even if card fails)
				if (shouldSampleAuthProviderSucceeded()) {
					fireMediaCardEvent(
						getAuthProviderSucceededPayload(
							authEvent.payload.durationMs,
							authEvent.payload.timeoutMs,
							authEvent.payload.authContext,
						),
						createAnalyticsEvent,
					);
				}
			} else if (authEvent.type === 'failed') {
				// Always emit failed events (no sampling)
				fireMediaCardEvent(
					getAuthProviderFailedPayload(
						authEvent.payload.durationMs,
						authEvent.payload.timeoutMs,
						authEvent.payload.error,
						authEvent.payload.authContext,
					),
					createAnalyticsEvent,
				);
			}
			pendingAuthProviderEventRef.current = null;
		}
		// Determine SSR preview info for UFO timing strategy
		// wasSSRAttempted is only true when SSR was used AND preview exists AND preview is non-lazy
		// because lazy SSR defers loading, so it behaves like CSR for timing purposes
		// We also require preview to exist to avoid false positives when:
		// 1. ssr='client' but no SSR data exists (client-side navigation)
		// 2. Non-previewable files (e.g., zip files) where no preview is generated
		const isSSRNonLazy = !!ssr && !!preview && preview.lazy !== true;
		const wasSSRSuccessful =
			isSSRNonLazy &&
			(ssrReliability.server?.status === 'success' || ssrReliability.client?.status === 'success');

		const ssrPreviewInfo: SSRPreviewInfo = {
			dataUri: preview?.dataURI,
			srcset: preview?.srcSet,
			wasSSRAttempted: isSSRNonLazy,
			wasSSRSuccessful,
		};

		ufoExperience.complete(
			finalStatus,
			fileAttributes,
			fileStateFlagsRef.current,
			ssrReliability,
			finalError,
			ssrPreviewInfo,
		);
	});

	const fireNonCriticalErrorEventRef = useCurrentValueRef((error: MediaCardError) => {
		createAnalyticsEvent &&
			fireNonCriticalErrorEvent(
				createAnalyticsEvent,
				finalStatus,
				fileAttributes,
				ssrReliability,
				error,
				traceContext,
				fileStateValue?.metadataTraceContext,
			);
	});

	useEffect(() => {
		if (nonCriticalError) {
			fireNonCriticalErrorEventRef.current(nonCriticalError);
		}
	}, [nonCriticalError, fireNonCriticalErrorEventRef]);

	const startUfoExperienceRef = useCurrentValueRef(() => {
		// For non-lazy SSR previews, use interaction start time as the experience start time
		// because the image loading started at interaction start, not at mount time
		const isSSRNonLazy = !!ssr && preview?.lazy !== true;
		ufoExperience.start({ useInteractionTime: isSSRNonLazy });
	});

	const fireAbortedEventRef = useCurrentValueRef(() => {
		// UFO won't abort if it's already in a final state (succeeded, failed, aborted, etc)
		ufoExperience.abort({
			fileAttributes,
			fileStateFlags: fileStateFlagsRef?.current,
			ssrReliability: ssrReliability,
		});
	});

	// Listen to auth provider events and store them (don't emit yet)
	useEffect(() => {
		if (!fg('platform_media_auth_provider_analytics')) {
			return;
		}

		const onAuthSuccess = (payload: AuthProviderSucceededEventPayload) => {
			// Store latest auth provider event for later emission when card status changes
			pendingAuthProviderEventRef.current = { type: 'succeeded', payload };
		};

		const onAuthFailed = (payload: AuthProviderFailedEventPayload) => {
			// Store latest auth provider event for later emission when card status changes
			pendingAuthProviderEventRef.current = { type: 'failed', payload };
		};

		globalMediaEventEmitter.on('auth-provider-succeeded', onAuthSuccess);
		globalMediaEventEmitter.on('auth-provider-failed', onAuthFailed);

		return () => {
			globalMediaEventEmitter.off('auth-provider-succeeded', onAuthSuccess);
			globalMediaEventEmitter.off('auth-provider-failed', onAuthFailed);
		};
	}, []);

	//----------------------------------------------------------------//
	//--------------------- Handling Errors---------------------------//
	//----------------------------------------------------------------//

	useEffect(() => {
		if (onErrorRef.current && finalError) {
			const errorReason = finalError.primaryReason;
			onErrorRef.current(errorReason);
		}
	}, [finalError, onErrorRef]);

	//----------------------------------------------------------------//
	//--------------------- Focus on Close Viewer  -------------------//
	//----------------------------------------------------------------//

	const wasViewerPreviouslyOpen = usePrevious(mediaViewerSelectedItem);

	useEffect(() => {
		if (wasViewerPreviouslyOpen && !mediaViewerSelectedItem) {
			mediaViewerButtonRef.current?.focus();
		}
	}, [wasViewerPreviouslyOpen, mediaViewerSelectedItem]);

	//----------------------------------------------------------------//
	//---------------------- Callbacks & Handlers  -------------------//
	//----------------------------------------------------------------//

	const onSvgError = (error: MediaCardError) => {
		setError(error);
		setStatus('error');
	};

	const onImageError = (newCardPreview?: MediaFilePreview) => {
		if (metadata.mimeType === 'image/svg+xml') {
			return;
		}
		onImageErrorBase(newCardPreview);
	};

	const onSvgLoad = () => {
		setPreviewDidRender(true);
		onPreviewRender?.(identifier.id);
	};

	const onImageLoad = (newCardPreview?: MediaFilePreview) => {
		if (metadata.mimeType === 'image/svg+xml') {
			return;
		}
		onImageLoadBase(newCardPreview);
		setPreviewDidRender(true);
		onPreviewRender?.(identifier.id);
	};

	const onCardClick = (
		event: React.MouseEvent<HTMLDivElement | HTMLButtonElement>,
		analyticsEvent?: UIAnalyticsEvent,
	) => {
		if (onClick) {
			const cardEvent = {
				event,
				mediaItemDetails: metadata,
			};
			onClick(cardEvent, analyticsEvent);
		}
	};

	const onCardViewClick = (
		event: React.MouseEvent<HTMLDivElement | HTMLButtonElement>,
		analyticsEvent?: UIAnalyticsEvent,
	) => {
		onCardClick(event, analyticsEvent);

		if (!metadata) {
			return;
		}

		const isVideo = metadata.mediaType === 'video';

		const isBrowserSupported = metadata.mimeType && isMimeTypeSupportedByBrowser(metadata.mimeType);

		// TODO: this should be handled by Inline Player
		const isPlayable =
			!!fileState &&
			(fileState.status === 'processed' ||
				(isBrowserSupported && ['processing', 'uploading'].includes(fileState.status)));

		if (useInlinePlayer && isVideo && isPlayable && finalStatus !== 'error') {
			setIsPlayingFile(true);
			setShouldAutoplay(true);
		} else if (shouldOpenMediaViewer) {
			openMediaViewer();
		}

		// Abort VC when click file card
		pressTracing();
	};

	//----------------------------------------------------------------//
	//----------------- update status flags --------------------------//
	//----------------------------------------------------------------//

	useEffect(() => {
		if (fileStateValue?.status === 'processing') {
			fileStateFlagsRef.current.wasStatusProcessing = true;
		} else if (fileStateValue?.status === 'uploading') {
			fileStateFlagsRef.current.wasStatusUploading = true;
		}
	}, [fileStateValue?.status]);

	//----------------------------------------------------------------//
	//---------------- fetch and resolve card preview ----------------//
	//----------------------------------------------------------------//

	const prevStatus = usePrevious(finalStatus);
	const prevIsCardVisible = usePrevious(isCardVisible);

	useEffect(() => {
		if (prevStatus !== undefined && finalStatus !== prevStatus) {
			fireOperationalEventRef.current();
		}
	}, [fireOperationalEventRef, prevStatus, finalStatus]);

	useEffect(() => {
		const turnedVisible = !prevIsCardVisible && isCardVisible;

		if (turnedVisible) {
			startUfoExperienceRef.current();
		}
	}, [startUfoExperienceRef, isCardVisible, prevIsCardVisible]);

	//----------------------------------------------------------------//
	//----------------- set isPlayingFile state ----------------------//
	//----------------------------------------------------------------//

	useEffect(() => {
		const isVideo = fileAttributes.fileMediatype === 'video';

		const { mimeType } = getFileDetails(identifier, fileStateValue);
		const isVideoPlayable =
			(mimeType && isVideoMimeTypeSupportedByBrowser(mimeType)) ||
			(fileStateValue && isProcessedFileState(fileStateValue));

		if (
			/**
			 * We need to check that the card is visible before switching to inline player
			 * in order to avoid race conditions of the ViewportDector being unmounted before
			 * it is able to set isCardVisible to true.
			 */
			isCardVisible &&
			isVideo &&
			!isPlayingFile &&
			disableOverlay &&
			useInlinePlayer &&
			isVideoPlayable &&
			fileStateValue?.status !== 'failed-processing' &&
			finalStatus !== 'error'
		) {
			setIsPlayingFile(true);
		}
	}, [
		isCardVisible,
		disableOverlay,
		fileAttributes.fileMediatype,
		fileStateValue,
		identifier,
		isPlayingFile,
		finalStatus,
		useInlinePlayer,
	]);

	//----------------------------------------------------------------//
	//----------------- abort UFO experience -------------------------//
	//----------------------------------------------------------------//

	useEffect(() => {
		return () => {
			// eslint-disable-next-line react-hooks/exhaustive-deps
			fireAbortedEventRef.current();
		};
	}, [fireAbortedEventRef]);

	//----------------------------------------------------------------//
	// Update Status
	//----------------------------------------------------------------//

	const updateFileStateRef = useCurrentValueRef(() => {
		// This effect has race condition with Complete effect. We share the same check to ovid status overrides

		// do not update the card status if the status is final
		if (['complete', 'error', 'failed-processing'].includes(finalStatus)) {
			return;
		}

		if (
			previewDidRender &&
			// We should't complete if status is uploading
			['loading-preview', 'processing'].includes(finalStatus)
		) {
			setStatus('complete');
			// TODO MEX-788: add test for "do not remove the card preview when unsubscribing".
		} else if (fileState) {
			if (fileState.status !== 'error') {
				let newStatus: CardStatus;
				switch (fileState.status) {
					case 'uploading':
					case 'failed-processing':
					case 'processing':
						newStatus = fileState.status;
						break;
					case 'processed':
						// Set complete if processing is done and there is no preview
						if (!preview && previewStatus === 'complete') {
							newStatus = 'complete';
							break;
						}
						newStatus = 'loading-preview';
						break;
					default:
						newStatus = 'loading';
				}

				const newProgress =
					newStatus === 'uploading' && fileState.status === 'uploading' ? fileState.progress : 1;

				setStatus(newStatus);
				uploadProgressRef.current = newProgress;
			} else {
				const e = toCommonMediaClientError(fileState);

				const errorReason = finalStatus === 'uploading' ? 'upload' : 'metadata-fetch';
				setError(new MediaCardError(errorReason, e));
				setStatus('error');
			}
		}
	});
	const mergedRef = useMergeRefs<HTMLDivElement>([setCardElement, copyNodeRef]);
	useEffect(() => {
		updateFileStateRef.current();
	}, [fileState, preview, previewStatus, updateFileStateRef, previewDidRender, finalStatus]);

	//----------------------------------------------------------------//
	// Shared Card View & SVG View resources
	//----------------------------------------------------------------//

	const mediaCardCursor = getMediaCardCursor(
		!!useInlinePlayer,
		!!shouldOpenMediaViewer,
		finalStatus === 'error' || finalStatus === 'failed-processing',
		!!preview,
		metadata.mediaType,
	);

	const onImageMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
		onMouseEnter?.({
			event,
			mediaItemDetails: metadata,
		});
	};

	const onImageFocus = (event: React.FocusEvent<HTMLDivElement>) => {
		if (!isKeyboardFocusEnteringElement(event)) {
			return;
		}
		onFocus?.({
			event,
			mediaItemDetails: metadata,
		});
	};

	//----------------------------------------------------------------//
	//---------------------- Render Card Function --------------------//
	//----------------------------------------------------------------//

	const renderCard = (
		withCallbacks = true,
		cardStatusOverride?: CardStatus,
		izLazyOverride?: boolean,
	) => {
		const { mediaItemType } = identifier;

		const isLazyWithOverride = izLazyOverride === undefined ? isLazy : izLazyOverride;

		let isLazyPreview = fg('media-perf-lazy-loading-optimisation') ? preview?.lazy : true;

		// We should natively lazy load an SSR preview when card is not visible,
		// otherwise we'll fire the metadata fetch from outside the viewport
		// Side note: We should not lazy load if the cardPreview is available from local cache,
		// in order to avoid flickers during re-mount of the component
		// CXP-2723 TODO: Create test cases for the above scenarios
		const nativeLazyLoad =
			isLazyWithOverride &&
			(fg('media-perf-uplift-mutation-fix') || !isCardVisible) &&
			preview &&
			isSSRPreview(preview) &&
			isLazyPreview;
		// Force Media Image to always display img for SSR
		const forceSyncDisplay = !!ssr;

		const card = (
			<CardView
				identifier={identifier}
				status={cardStatusOverride || finalStatus}
				error={finalError}
				mediaItemType={mediaItemType}
				metadata={metadata}
				cardPreview={preview}
				alt={alt}
				resizeMode={resizeMode}
				dimensions={cardDimensions}
				actions={computedActions}
				selectable={selectable}
				selected={selected}
				shouldOpenMediaViewer={shouldOpenMediaViewer}
				openMediaViewerButtonRef={mediaViewerButtonRef}
				onClick={withCallbacks ? onCardViewClick : undefined}
				onMouseEnter={withCallbacks ? onImageMouseEnter : undefined}
				onFocus={withCallbacks ? onImageFocus : undefined}
				disableOverlay={disableOverlay}
				progress={uploadProgressRef.current}
				onDisplayImage={
					withCallbacks
						? () => {
								const payloadPart = {
									fileId: identifier.id,
									isUserCollection: identifier.collectionName === RECENTS_COLLECTION,
								};

								globalMediaEventEmitter.emit('media-viewed', {
									viewingLevel: 'minimal',
									...payloadPart,
								});
							}
						: undefined
				}
				innerRef={mergedRef}
				testId={testId}
				titleBoxBgColor={titleBoxBgColor}
				titleBoxIcon={titleBoxIcon}
				backgroundColor={backgroundColor}
				onImageError={withCallbacks ? onImageError : undefined}
				onImageLoad={withCallbacks ? onImageLoad : undefined}
				onSvgError={onSvgError}
				onSvgLoad={onSvgLoad}
				nativeLazyLoad={nativeLazyLoad}
				forceSyncDisplay={forceSyncDisplay}
				traceId={traceContext.traceId}
				mediaCardCursor={mediaCardCursor}
				shouldHideTooltip={shouldHideTooltip}
				overriddenCreationDate={overridenDate}
				isAIGenerating={isAIGenerating}
				isCWR={isCWR}
				hasLoadingMotion={hasLoadingMotion}
			/>
		);

		return isLazyWithOverride ? (
			<ViewportDetector
				cardEl={cardElement}
				onVisible={() => {
					setIsCardVisible(true);
				}}
			>
				{card}
			</ViewportDetector>
		) : (
			card
		);
	};

	//----------------------------------------------------------------//
	//-------------------------- RENDER ------------------------------//
	//----------------------------------------------------------------//

	const inlinePlayerFallback = isPlayingFile ? renderCard(false, 'loading', false) : <></>;
	const collectionName = identifier.collectionName || '';

	return (
		<>
			{!!fileStateValue?.abuseClassification && (
				<AbuseModal
					isOpen={isAbuseModalOpen}
					onConfirm={downloadFn}
					onClose={() => setIsAbuseModalOpen(false)}
				/>
			)}
			{isPlayingFile ? (
				<Suspense fallback={inlinePlayerFallback}>
					<InlinePlayerLazy
						dimensions={cardDimensions}
						originalDimensions={originalDimensions}
						identifier={identifier}
						autoplay={!!shouldAutoplay}
						onFullscreenChange={onFullscreenChange}
						onError={(e) => {
							setError(new MediaCardError('error-file-state', e));
							setStatus('error');
							setIsPlayingFile(false);
						}}
						onClick={onCardClick}
						selected={selected}
						ref={setCardElement}
						testId={testId}
						cardPreview={preview}
						videoControlsWrapperRef={videoControlsWrapperRef}
					/>
				</Suspense>
			) : (
				renderCard()
			)}
			{mediaViewerSelectedItem ? (
				<MediaViewer
					collectionName={collectionName}
					items={mediaViewerItems || []}
					mediaClientConfig={mediaClient.config}
					selectedItem={mediaViewerSelectedItem}
					onClose={closeMediaViewer}
					contextId={contextId}
					featureFlags={featureFlags}
					viewerOptions={viewerOptions}
					extensions={mediaViewerExtensions}
					fallbackMediaNameFetcher={fallbackMediaNameFetcher}
				/>
			) : null}
			{/* Print the SSR result to be used during hydration */}
			{getSsrScriptProps && <script {...getSsrScriptProps()} />}
		</>
	);
};
